import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '@kmgestao/i18n';
import { Button, toast } from '@kmgestao/ui';

import { useResendOwnerInvite } from '../../../features/clinics/index.js';
import {
  formatPhoneBR,
  type Lead,
  LEAD_STAGE_LABELS,
  type ProvisionPlan,
} from '../../../features/leads/index.js';
import { ProvisionClinicDialog } from './ProvisionClinicDialog.js';

/**
 * Banner exibido no topo do detalhe do lead quando ele atingiu estado
 * terminal (`signed` ou `provisioned`) — ou seja, virou cliente.
 *
 * Substitui visualmente o `PageIntroCard` no momento da conversão pra
 * dar peso ao marco. Reportado pelo Alexandre 2026-04-25:
 *   - "Lead aceitou proposta de R$ 18k de consultoria + plano Pro e a UI
 *      mostrava chip cinza minúsculo. Tem que celebrar visualmente."
 *
 * Visual:
 *   - Background tonificado accent (gold sutil)
 *   - Borda accent/40
 *   - Eyebrow uppercase tracking-wider em accent-foreground
 *   - Título grande
 *   - Lista de fatos-chave (representante, email, telefone)
 *   - CTAs por estágio:
 *     - `signed`: "Provisionar clínica agora" (habilitado), "Acessar painel" (disabled)
 *     - `provisioned`: "Acessar painel da clínica" (link real ou disabled)
 *
 * Strings em pt-BR vêm do namespace i18n `clinics` (regra inviolável
 * §5.3 do AGENTS.md). Usa tokens do DS: accent, accent-foreground,
 * foreground, muted-foreground, border. Light + dark sem hardcode.
 */
export interface LeadCustomerBannerProps {
  lead: Lead;
  /** ID do contrato signed pra ancorar o provisionamento. Quando ausente,
   * o botão "Provisionar" não aparece — caso de borda em que webhook
   * ainda não atualizou o contract no banco. */
  signedContractId?: string | null;
  /** Plano de provisionamento computado a partir dos dados da proposta
   * (preço SaaS efetivo após desconto/saas_free, trial efetivo,
   * total da consultoria, status inicial, gate mode). UI usa pra
   * renderizar dialog correto. Single source of truth (mesmo helper
   * que o edge function usa). */
  signedProvisionPlan?: ProvisionPlan | null;
  signedBillingCycle?: string | null;
  /** ID da clínica provisionada — habilita o link "Acessar painel da
   * clínica" (quando owner aceitou) ou estado intermediário "Aguardando
   * ativação" (quando member ainda 'invited'). */
  clinicId?: string | null;
  /** Status do owner member da clínica — decide entre estado
   * "Aguardando aceite" (com botão reenviar) e "Cliente ativo" (com
   * link pro painel). */
  ownerMemberStatus?: 'invited' | 'active' | 'suspended' | null;
  /** Status da subscription da clínica — quando provisionada com gate
   * de pagamento (Modelo C híbrido — combo SaaS-free + consultoria, ou
   * SaaS sem trial), começa em `'incomplete'`. Banner usa pra diferenciar
   * "aguardando pagamento" (incomplete) de "aguardando aceite do invite"
   * (active + member invited). Karla precisa saber se vale a pena cobrar
   * pagamento ou só esperar lead clicar no link. */
  subscriptionStatus?:
    | 'incomplete'
    | 'trialing'
    | 'active'
    | 'past_due'
    | 'canceled'
    | 'paused'
    | null;
}

export function LeadCustomerBanner({
  lead,
  signedContractId,
  signedProvisionPlan,
  signedBillingCycle,
  clinicId,
  ownerMemberStatus,
  subscriptionStatus,
}: LeadCustomerBannerProps) {
  const { t } = useT('clinics');
  const isProvisioned = lead.stage === 'provisioned';
  const isSigned = lead.stage === 'signed';
  const conversionDate = formatLeadConversionDate(lead);
  const fantasy = lead.clinic_name_guess ?? lead.full_name;

  const [provisionOpen, setProvisionOpen] = useState(false);
  const resendInvite = useResendOwnerInvite();

  // Estado canônico do banner — 4 mutuamente exclusivos (após PR
  // 2026-04-26 que separou "aguardando pagamento" de "aguardando aceite"):
  //   1. `awaiting-payment`: lead signed + clinic provisionada com gate
  //      de pagamento ativo (subscription `'incomplete'`). Cliente
  //      precisa pagar 1ª parcela. Karla NÃO deve reenviar invite
  //      (cliente nem viu o convite ainda — Asaas é quem manda cobrança).
  //      Mostra orientação clara + botão pra ver o painel.
  //   2. `awaiting-acceptance`: lead signed + clinic provisionada +
  //      subscription ativa MAS owner ainda não aceitou (member `'invited'`).
  //      Cliente já pagou mas não clicou no link de ativação.
  //      Karla pode reenviar invite (botão habilitado).
  //   3. `ready-to-provision`: lead signed + sem clinic provisionada.
  //      Mostra "Ativar clínica agora" (abre dialog de confirmação).
  //   4. `customer-active`: lead provisioned + member 'active'. Mostra
  //      "Acessar painel da clínica".
  //
  // Hierarquia de checagem: subscription_status === 'incomplete' tem
  // prioridade sobre member status — porque até pagar, o member fica
  // 'invited' (provision-clinic não gera magic link com gate ativo).
  // Ver docs/incidents/2026-04-26-rajada-de-regressoes.md Bug 8 +
  // ADR 0009 §"Atualização 2026-04-26" pra contexto completo.
  const bannerState:
    | 'awaiting-payment'
    | 'awaiting-acceptance'
    | 'ready-to-provision'
    | 'customer-active'
    | 'idle' =
    isProvisioned && clinicId
      ? 'customer-active'
      : isSigned && clinicId && subscriptionStatus === 'incomplete'
        ? 'awaiting-payment'
        : isSigned && clinicId && ownerMemberStatus === 'invited'
          ? 'awaiting-acceptance'
          : isSigned && signedContractId
            ? 'ready-to-provision'
            : 'idle';

  const handleResend = async () => {
    if (!clinicId) return;
    await resendInvite.mutateAsync(
      { clinic_id: clinicId },
      {
        onSuccess: (result) => {
          if (result.action === 'already_accepted') {
            toast.info(t('detail.members.resendInvite.alreadyAccepted'));
          } else if (result.email_status === 'sent') {
            toast.success(t('detail.members.resendInvite.successTitle'), {
              description: t('detail.members.resendInvite.successDescription'),
            });
          } else {
            toast.error(t('detail.members.resendInvite.failedTitle'), {
              description: t('detail.members.resendInvite.failedDescription'),
            });
          }
        },
        onError: (err) =>
          toast.error(t('detail.members.resendInvite.failedTitle'), {
            description: (err as Error).message,
          }),
      },
    );
  };

  return (
    <section
      className="relative overflow-hidden rounded-xl border border-accent/40 bg-accent/5 p-6 shadow-sm sm:p-8"
      aria-labelledby="lead-customer-banner-title"
    >
      {/* Glow decorativo no canto pra dar "sensação de marco" — sutil
          (accent/8) pra não competir com conteúdo. Mesma família visual
          das bandas da proposta pública. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-16 -top-16 size-64 rounded-full bg-[radial-gradient(circle_at_center,theme(colors.accent/0.12),transparent_70%)]"
      />

      <div className="relative z-10 space-y-4">
        <div className="space-y-1">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-accent-foreground">
            {bannerState === 'customer-active'
              ? t('banner.eyebrowProvisioned')
              : bannerState === 'awaiting-payment'
                ? t('banner.eyebrowAwaitingPayment')
                : bannerState === 'awaiting-acceptance'
                  ? t('banner.eyebrowAwaitingAcceptance')
                  : t('banner.eyebrowSigned')}
          </p>
          <h1
            id="lead-customer-banner-title"
            className="text-2xl font-bold text-foreground sm:text-3xl"
          >
            {fantasy}
          </h1>
          <p className="text-sm text-muted-foreground">
            {LEAD_STAGE_LABELS[lead.stage]}
            {conversionDate ? ` · ${conversionDate}` : null}
          </p>
        </div>

        <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
          <div>
            <dt className="text-xs uppercase tracking-wider text-muted-foreground">
              {t('banner.fields.representative')}
            </dt>
            <dd className="font-medium text-foreground">{lead.full_name}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wider text-muted-foreground">
              {t('banner.fields.email')}
            </dt>
            <dd className="break-all font-medium text-foreground">{lead.email}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wider text-muted-foreground">
              {t('banner.fields.phone')}
            </dt>
            <dd className="font-medium text-foreground">
              {lead.phone ? formatPhoneBR(lead.phone) : '—'}
            </dd>
          </div>
        </dl>

        {/* Estado AWAITING-PAYMENT: clinic provisionada com gate de
            pagamento (subscription `'incomplete'`). Cliente recebeu
            cobrança do Asaas e ainda não pagou — provision-clinic NÃO
            gerou magic link nem mandou welcome email (defere pro
            asaas-webhook após PAYMENT_CONFIRMED). Karla NÃO deve
            reenviar invite (cliente nem viu invite ainda — só recebeu
            cobrança). Orientação clara: aguardar pagamento. */}
        {bannerState === 'awaiting-payment' ? (
          <div className="rounded-md border border-warning/40 bg-warning/5 p-3 text-sm">
            <p className="font-semibold text-foreground">{t('banner.awaitingPayment.title')}</p>
            <p className="mt-1 text-muted-foreground">
              {t('banner.awaitingPayment.description', { email: lead.email })}
            </p>
          </div>
        ) : null}

        {/* Estado AWAITING-ACCEPTANCE: clinic provisionada + subscription
            ativa (cliente JÁ pagou OU plano sem gate) MAS owner ainda
            não clicou no link de ativação (member 'invited'). Banner
            amarelo informativo + botão "Reenviar email de ativação".
            Resolve o caso reportado em 2026-04-25 onde clicar "Ativar
            clínica agora" retornava cached sem reenviar email. */}
        {bannerState === 'awaiting-acceptance' ? (
          <div className="rounded-md border border-warning/40 bg-warning/5 p-3 text-sm">
            <p className="font-semibold text-foreground">{t('banner.awaitingAcceptance.title')}</p>
            <p className="mt-1 text-muted-foreground">
              {t('banner.awaitingAcceptance.description', { email: lead.email })}
            </p>
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-2 pt-2">
          {bannerState === 'ready-to-provision' ? (
            <Button type="button" size="lg" onClick={() => setProvisionOpen(true)}>
              {t('banner.actions.provision')}
            </Button>
          ) : null}

          {/* Awaiting-payment: SEM botão de reenviar invite — cliente
              nem viu o convite ainda (defere pro asaas-webhook). Só
              link pra Karla acompanhar o status no painel da clínica. */}
          {bannerState === 'awaiting-payment' && clinicId ? (
            <Button asChild size="lg" variant="outline">
              <Link to={`/superadmin/clinics/${clinicId}`}>{t('banner.actions.openClinic')}</Link>
            </Button>
          ) : null}

          {bannerState === 'awaiting-acceptance' && clinicId ? (
            <>
              <Button
                type="button"
                size="lg"
                onClick={handleResend}
                disabled={resendInvite.isPending}
              >
                {resendInvite.isPending
                  ? t('detail.members.resendInvite.sending')
                  : t('banner.actions.resendInvite')}
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to={`/superadmin/clinics/${clinicId}`}>{t('banner.actions.openClinic')}</Link>
              </Button>
            </>
          ) : null}

          {bannerState === 'customer-active' && clinicId ? (
            <Button asChild size="lg" variant="outline">
              <Link to={`/superadmin/clinics/${clinicId}`}>{t('banner.actions.openClinic')}</Link>
            </Button>
          ) : null}

          {/* Caso de borda: provisioned mas sem clinicId (backfill antigo). */}
          {isProvisioned && !clinicId ? (
            <Button
              type="button"
              size="lg"
              variant="outline"
              disabled
              title={t('banner.actions.openClinicMissing')}
            >
              {t('banner.actions.openClinic')}
            </Button>
          ) : null}

          {/* Caso de borda: signed sem contract row (sincronia). */}
          {bannerState === 'idle' && isSigned ? (
            <p className="ml-1 self-center text-sm text-muted-foreground">
              {t('banner.actions.contractSyncing')}
            </p>
          ) : null}
        </div>
      </div>

      {isSigned && signedContractId && signedProvisionPlan ? (
        <ProvisionClinicDialog
          leadId={lead.id}
          contractId={signedContractId}
          clinicNameHint={fantasy}
          ownerEmail={lead.email}
          provisionPlan={signedProvisionPlan}
          billingCycle={signedBillingCycle ?? 'monthly'}
          open={provisionOpen}
          onOpenChange={setProvisionOpen}
        />
      ) : null}
    </section>
  );
}

/** Tenta extrair a data de conversão (assinatura ou provisionamento) do
 * lead. Como não temos colunas dedicadas pra `signed_at`/`provisioned_at`
 * em leads (esses dados moram em contracts/clinics), por ora usamos
 * `updated_at` como fallback razoável — que reflete a última transição
 * de stage. PR futuro pode trazer o timestamp exato via join.
 *
 * Retorna a data formatada em pt-BR ou null se inválida. */
function formatLeadConversionDate(lead: Lead): string | null {
  const iso = lead.updated_at ?? lead.created_at;
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return null;
  }
}
